import {
  ApiResponse,
  PaginationParams,
  SortingParams,
} from "@src/types/httpApi";
import {
  ICreateMoodBody,
  IMood,
  IMoodFactory,
  IMoodsFilters,
} from "@src/types/mood";
import { CreatePlaylistBody } from "@src/types/playlist";
import httpApi from "@utils/httpApi";
import { AxiosResponse } from "axios";

export const fetchMoods = (
  filtersApplied: IMoodsFilters & { sorting: SortingParams } & PaginationParams
): Promise<AxiosResponse<ApiResponse<IMood[]>>> => {
  const queryParams = new URLSearchParams();
  if (filtersApplied.sorting.field)
    queryParams.append(
      "sort",
      `${filtersApplied.sorting.field}:${filtersApplied.sorting.direction}`
    );
  if (filtersApplied.title || filtersApplied.searchText) {
    if (filtersApplied.searchText)
      queryParams.append(
        "filters[title][$containsi]",
        String(filtersApplied.searchText).toLowerCase().trim()
      );
    else
      queryParams.append(
        "filters[title][$containsi]",
        String(filtersApplied.title).toLowerCase().trim()
      );
  }
  if (filtersApplied?.page) {
    queryParams.append("pagination[page]", String(filtersApplied.page));
  }
  if (filtersApplied?.pageSize) {
    queryParams.append("pagination[pageSize]", String(filtersApplied.pageSize));
  }
  return httpApi.get(`/api/moods?${queryParams.toString()}`);
};

export const fetchFactoryMoods = (
  filtersApplied: IMoodsFilters & { sorting: SortingParams } & PaginationParams
): Promise<AxiosResponse<ApiResponse<IMoodFactory[]>>> => {
  const queryParams = new URLSearchParams();
  if (filtersApplied.sorting.field)
    queryParams.append(
      "sort",
      `${filtersApplied.sorting.field}:${filtersApplied.sorting.direction}`
    );
  if (filtersApplied.searchText) {
    if (filtersApplied.searchText)
      queryParams.append(
        "filters[title][$containsi]",
        String(filtersApplied.searchText).toLowerCase().trim()
      );
  }
  if (filtersApplied?.page) {
    queryParams.append("pagination[page]", String(filtersApplied.page));
  }
  if (filtersApplied?.pageSize) {
    queryParams.append("pagination[pageSize]", String(filtersApplied.pageSize));
  }
  return httpApi.get(`/api/mood-factories?${queryParams.toString()}`);
};

export const createMoodApi = async (
  createMoodBody: ICreateMoodBody
): Promise<AxiosResponse<any>> => {
  return httpApi.post("/api/moods", {
    data: createMoodBody,
  });
};

export const updateMoodApi = async (
  createPlaylistBody: Partial<CreatePlaylistBody>,
  playlistId: string
): Promise<AxiosResponse<any>> => {
  return httpApi.put(`/api/moods/${playlistId}`, {
    data: {
      title: createPlaylistBody.playlistName,
      notes: createPlaylistBody.notes,
      overwriteShowDescription: createPlaylistBody.overwriteShowDescription,
      curator: createPlaylistBody.curator,
      show: {
        set: [createPlaylistBody.assignedShow],
      },
    },
  });
};

export const deleteMoodApi = async (
  playlistId: string
): Promise<AxiosResponse<any>> => {
  return httpApi.delete(`/api/moods/${playlistId}`);
};

export const fetchPlaylistTracksApi = (
  playlistId: string
): Promise<AxiosResponse<any>> => {
  return httpApi.get(`/api/playlists/${playlistId}`, {
    params: {
      populate: {
        tracks: {
          populate: {
            audio: {
              fields: ["*"],
            },
            tags: {
              fields: ["*"],
            },
            genre: {
              fields: ["*"],
            },
            emotion: {
              fields: ["*"],
            },
            moods: {
              fields: ["*"],
            },
          },
        },
      },
      fields: ["documentId"],
    },
  });
};
export const fetchMoodDetailsApi = (moodId: string): Promise<AxiosResponse> => {
  return httpApi.get(`api/moods/${moodId}`);
};
export const updateMoodTrackOrderApi = (
  moodId: string,
  tracksIds: string[]
): Promise<AxiosResponse> => {
  return httpApi.put(`api/playlists/${moodId}`, {
    data: {
      tracks: {
        set: tracksIds,
      },
    },
  });
};
